// "Sales Price" side panel (bottom of the right column) — the price this
// configuration resolves to in the NDB price list, shown live before submitting.
//
// Deliberately just the heading and the amount. The key values behind it
// (housing type / design / size / height), the workbook reference and the
// reason a price didn't resolve all live in the panel's tooltip, so the panel
// stays a single glanceable number without losing the diagnostics.
//
// The lookup needs all four key values filled and one price on file for them
// (see util/ndbPricing.ts); anything else shows "No list price", which is
// exactly what Save & Submit will do — set no price at all, leaving the item's
// default base price and the sales-order line's placeholder amount.
//
// Debounced on the resolved key (not on `answers`, which is a fresh object on
// every keystroke), so typing in an unrelated field never re-queries.

import { useEffect, useState } from "react";
import { useAnswers } from "../state/AnswersContext";
import {
  buildNdbPriceKey,
  lookupNdbPrice,
  describeNdbPriceKey,
  hasSelectedHousing,
  type NdbPriceKey,
  type NdbPriceResult,
} from "../util/ndbPricing";

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

// Stable string for the four key values, so the effect only re-runs when the
// housing being priced actually changes.
function keySignature(key: NdbPriceKey | null): string {
  return key ? `${key.housingType}|${key.design}|${key.size}|${key.height}` : "";
}

export default function PricePanel() {
  const { answers } = useAnswers();
  const built = buildNdbPriceKey(answers);
  const key = built.key;
  const sig = keySignature(key);
  const missingReason = built.key ? null : (built as { reason: string }).reason;

  const [result, setResult] = useState<NdbPriceResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // No complete key -> nothing to look up.
    if (!sig) {
      setResult(null);
      setBusy(false);
      return;
    }
    let cancelled = false;
    setBusy(true);
    const t = window.setTimeout(() => {
      lookupNdbPrice(answers).then((r) => {
        if (cancelled) return;
        setResult(r);
        setBusy(false);
      });
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      setBusy(false);
    };
    // Keyed on the price-list key only — `answers` is read inside, deliberately
    // not a dependency (it changes on every keystroke).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const matched = result?.matched === true;

  // Everything that used to be printed below the amount, folded into the tooltip.
  const tooltip = matched
    ? `${describeNdbPriceKey(key)}${result.reference ? ` · ${result.reference}` : ""}\n` +
      "Set on the item record and the sales-order line."
    : `${describeNdbPriceKey(key)}\n` +
      (busy
        ? "Checking the price list…"
        : missingReason
          ? `Needs: ${missingReason}`
          : (result?.matched === false && result.reason) || "Not in the price list.") +
      "\nNo price is set: the item keeps its default price and the order line its placeholder amount.";

  return (
    <div className="cpq-price-panel" data-state={matched ? "priced" : "unpriced"} title={tooltip}>
      <header>Sales Price</header>
      {matched ? (
        <div className="cpq-price-amount">{money(result.price)}</div>
      ) : !hasSelectedHousing(answers) || busy ? (
        // Nothing to price yet (or the lookup is on its way): a quiet dash, not a warning.
        // "No list price" is kept for a housing the price list really has no price for.
        <div className="cpq-price-amount is-pending">
          <span aria-hidden>—</span>
          <span className="cpq-visually-hidden">Not priced yet</span>
        </div>
      ) : (
        <div className="cpq-price-amount is-none">No list price</div>
      )}
    </div>
  );
}
